import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'light' | 'dark';
export type FontSize = 'normal' | 'large';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer: Renderer2;
  private themeSubject = new BehaviorSubject<Theme>(this.getCurrentTheme());
  private highContrastSubject = new BehaviorSubject<boolean>(
    this.getHighContrastSetting(),
  );
  private fontSizeSubject = new BehaviorSubject<FontSize>(
    this.getFontSizeSetting(),
  );

  public readonly theme$ = this.themeSubject.asObservable();
  public readonly highContrast$ = this.highContrastSubject.asObservable();
  public readonly fontSize$ = this.fontSizeSubject.asObservable();

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.initTheme();
    this.initHighContrast();
    this.initFontSize();
  }

  private getCurrentTheme(): Theme {
    // Verificar si hay un tema almacenado en localStorage
    const savedTheme = localStorage.getItem('theme') as Theme | null;

    // Si hay un tema guardado, usarlo
    if (savedTheme) {
      return savedTheme;
    }

    // Si no hay tema guardado, verificar preferencia del sistema
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark';
    }

    // Por defecto, usar tema claro
    return 'light';
  }

  private getHighContrastSetting(): boolean {
    // Verificar si la configuración de alto contraste está almacenada en localStorage
    const savedHighContrast = localStorage.getItem('highContrast');
    return savedHighContrast === 'true';
  }

  private getFontSizeSetting(): FontSize {
    // Verificar si la configuración de tamaño de fuente está almacenada en localStorage
    const savedFontSize = localStorage.getItem('fontSize') as FontSize | null;
    return savedFontSize ?? 'normal';
  }

  private initTheme(): void {
    const theme = this.getCurrentTheme();
    this.setTheme(theme);
  }

  private initHighContrast(): void {
    const highContrast = this.getHighContrastSetting();
    this.setHighContrast(highContrast);
  }

  private initFontSize(): void {
    const fontSize = this.getFontSizeSetting();
    this.setFontSize(fontSize);
  }

  public setTheme(theme: Theme): void {
    // Si alto contraste está activo, no cambiar el tema
    if (this.highContrastSubject.getValue()) {
      return;
    }

    localStorage.setItem('theme', theme);
    this.themeSubject.next(theme);

    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  public toggleTheme(): void {
    const currentTheme = this.themeSubject.getValue();
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  public setHighContrast(enabled: boolean): void {
    localStorage.setItem('highContrast', enabled.toString());
    this.highContrastSubject.next(enabled);

    if (enabled) {
      // Si alto contraste está activado, forzar también la clase 'dark'
      document.documentElement.classList.add('high-contrast');
      document.documentElement.classList.add('dark');
      // Actualizar el tema internamente sin cambiar el localStorage
      this.themeSubject.next('dark');
    } else {
      document.documentElement.classList.remove('high-contrast');
      // Restaurar el tema anterior cuando se desactiva el alto contraste
      const savedTheme = localStorage.getItem('theme') as Theme;
      if (savedTheme === 'light') {
        document.documentElement.classList.remove('dark');
        this.themeSubject.next('light');
      } else {
        this.themeSubject.next('dark');
      }
    }
  }

  public toggleHighContrast(): void {
    const currentSetting = this.highContrastSubject.getValue();
    this.setHighContrast(!currentSetting);
  }

  public setFontSize(size: FontSize): void {
    localStorage.setItem('fontSize', size);
    this.fontSizeSubject.next(size);

    if (size === 'large') {
      document.documentElement.classList.add('large-font');
    } else {
      document.documentElement.classList.remove('large-font');
    }
  }

  public toggleFontSize(): void {
    const currentSize = this.fontSizeSubject.getValue();
    const newSize = currentSize === 'normal' ? 'large' : 'normal';
    this.setFontSize(newSize);
  }
}
